import { useEffect, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { articles, siteInfo } from '@/mocks/data';

function renderContent(content: string) {
  const lines = content.split('\n');
  const elements: React.ReactNode[] = [];
  let i = 0;
  let key = 0;

  while (i < lines.length) {
    const line = lines[i];

    if (line.startsWith('## ')) {
      elements.push(
        <h2 key={key++} className="text-xl font-bold text-foreground-950 mt-10 mb-4" style={{ fontFamily: "var(--font-heading)" }}>
          {line.replace('## ', '')}
        </h2>,
      );
      i++;
    } else if (line.startsWith('### ')) {
      elements.push(
        <h3 key={key++} className="text-lg font-semibold text-foreground-950 mt-8 mb-3" style={{ fontFamily: "var(--font-heading)" }}>
          {line.replace('### ', '')}
        </h3>,
      );
      i++;
    } else if (line.startsWith('**') && line.endsWith('**')) {
      elements.push(
        <p key={key++} className="text-sm font-semibold text-foreground-900 mt-6 mb-2">
          {line.replace(/\*\*/g, '')}
        </p>,
      );
      i++;
    } else if (line.trim() === '') {
      i++;
    } else {
      let paragraph = line;
      i++;
      while (i < lines.length && lines[i].trim() !== '' && !lines[i].startsWith('## ') && !lines[i].startsWith('### ') && !(lines[i].startsWith('**') && lines[i].endsWith('**'))) {
        paragraph += '\n' + lines[i];
        i++;
      }
      const segments = paragraph.split(/(\*\*[^*]+\*\*)/g);
      const rendered = segments.map((seg, idx) => {
        if (seg.startsWith('**') && seg.endsWith('**')) {
          return (
            <strong key={idx} className="font-semibold text-foreground-950">
              {seg.replace(/\*\*/g, '')}
            </strong>
          );
        }
        return <span key={idx}>{seg}</span>;
      });

      elements.push(
        <p key={key++} className="text-sm text-foreground-700 leading-relaxed mb-4">
          {rendered}
        </p>,
      );
    }
  }

  return elements;
}

export default function ArticleDetail() {
  const { slug } = useParams<{ slug: string }>();

  const article = useMemo(() => articles.find((a) => a.slug === slug), [slug]);

  const relatedArticles = useMemo(
    () =>
      article
        ? articles.filter((a) => a.category === article.category && a.id !== article.id).slice(0, 3)
        : [],
    [article],
  );

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug]);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-foreground-950 mb-3">文章未找到</h1>
            <Link to="/" className="text-primary-500 hover:text-primary-600 cursor-pointer text-sm">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: '产品分类', href: '/category' },
    { label: article.categoryName, href: `/category?tab=${article.category}` },
    { label: article.title },
  ];

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.coverImage,
    datePublished: article.publishedAt,
    author: {
      '@type': 'Organization',
      name: siteInfo.name,
    },
    publisher: {
      '@type': 'Organization',
      name: siteInfo.name,
    },
  };

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }} />

      <Navbar />
      <ScrollToTop />

      <main className="pt-16">
        {/* Breadcrumb */}
        <div className="bg-background-100 px-4 md:px-6 py-4">
          <div className="max-w-4xl mx-auto">
            <Breadcrumb items={breadcrumbItems} />
          </div>
        </div>

        {/* Article Header */}
        <article className="px-4 md:px-6 py-8 md:py-12 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-2 mb-4">
              <span className="px-3 py-1 bg-secondary-100 text-secondary-700 rounded-full text-xs font-medium">
                {article.categoryName}
              </span>
              <span className="text-xs text-foreground-500">{article.publishedAt}</span>
              <span className="text-xs text-foreground-500">·</span>
              <span className="text-xs text-foreground-500">{article.readTime}</span>
            </div>

            <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-4 leading-tight" style={{ fontFamily: "var(--font-heading)" }}>
              {article.title}
            </h1>

            <p className="text-sm text-foreground-600 leading-relaxed mb-6">{article.excerpt}</p>

            <div className="flex flex-wrap gap-2 mb-8">
              {article.tags.map((tag) => (
                <span key={tag} className="px-2.5 py-1 bg-background-100 border border-background-200/70 rounded text-xs text-foreground-600">
                  #{tag}
                </span>
              ))}
            </div>

            {/* Cover Image */}
            <div className="rounded-lg overflow-hidden mb-10">
              <img
                src={article.coverImage}
                alt={article.title}
                className="w-full h-64 md:h-96 object-cover"
              />
            </div>

            {/* Content */}
            <div className="prose-custom">{renderContent(article.content)}</div>
          </div>
        </article>

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <section className="py-14 md:py-16 px-4 md:px-6 bg-background-100">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-xl font-bold text-foreground-950 mb-8" style={{ fontFamily: "var(--font-heading)" }}>
                相关文章
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.id}
                    to={`/article/${ra.slug}`}
                    className="group bg-background-50 border border-background-200/70 rounded-lg overflow-hidden hover:border-primary-300 transition-all cursor-pointer"
                  >
                    <div className="h-40 overflow-hidden">
                      <img
                        src={ra.coverImage}
                        alt={ra.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-4">
                      <h3 className="font-semibold text-foreground-950 text-sm mb-2 line-clamp-2 group-hover:text-primary-500 transition-colors" style={{ fontFamily: "var(--font-heading)" }}>
                        {ra.title}
                      </h3>
                      <p className="text-xs text-foreground-600 line-clamp-2 mb-3">{ra.excerpt}</p>
                      <div className="text-xs text-foreground-500">{ra.publishedAt}</div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
}